import { clearSessionToken, setSessionToken } from "../capacitor/session-token";
import type { ParsedUserAgent } from "../utils/userAgent";
import { http } from "../services/http";

// Takes pre-encoded JSON. On native the response body is the session token;
// on web the session is an HttpOnly cookie.
export async function login(credentials: string): Promise<unknown> {
  const data = await http.post<unknown>("/login", credentials);
  if (typeof data === "string" && data) {
    await setSessionToken(data);
  }
  return data;
}

// Clears the native bearer even on failure, so logging out offline still signs out.
export async function logout(): Promise<void> {
  try {
    await http.post("/logout");
  } finally {
    await clearSessionToken();
  }
}

// URL is the otpauth URL the QR code encodes.
export interface TotpEnrolment {
  Secret: string;
  URL: string;
  [field: string]: unknown;
}

// Each call makes a new secret.
export function getTotpEnrolment(options: { signal?: AbortSignal } = {}): Promise<TotpEnrolment> {
  return http.get<TotpEnrolment>("/otp/generate", options);
}

export function verifyTotp(body: { Code: string }): Promise<void> {
  return http.post("/otp/verify", body);
}

// An empty ID is the primary IdP.
export interface LoginIdp {
  ID: string;
  DisplayName: string;
}

// Unauthenticated.
export function getLoginIdps(options: { signal?: AbortSignal } = {}): Promise<LoginIdp[]> {
  return http.get<LoginIdp[]>("/login/idps", options);
}

// Swaps a native SAML login's one-time token for the session token.
export function exchangeAuthToken(token: string): Promise<{ sessionToken?: string } | null> {
  return http.post<{ sessionToken?: string } | null>("/auth/token-exchange", { token });
}

// A trust policy's identity check for a local account. A 429 carries Retry-After.
export function reauthenticate(body: { Password: string; TOTPCode: string; AclId: string }): Promise<void> {
  return http.post("/login/reauth", body);
}

export function changePassword(body: {
  CurrentPassword?: string | null;
  NewPassword?: string | null;
  ConfirmPassword?: string | null;
}): Promise<void> {
  return http.put("/password", body);
}

export interface SessionSlotGrant {
  knoc: string;
  ip?: string;
  expiresAt?: string;
}

// Mirrors services.SessionSlotConsumer on the backend.
export interface SessionSlotConsumer {
  kind: "session" | "agent" | "link";
  id: string;
  name?: string;
  ip?: string;
  authMethod?: string;
  userAgent?: string;
  parsedUserAgent?: ParsedUserAgent | null;
  createdAt: string;
  expiresAt?: string;
  // Agent kind only.
  status?: "pending_link" | "pending_approval" | "approved";
  statusChangedAt?: string;
  grants?: SessionSlotGrant[];
  current: boolean;
}

export interface SessionSlots {
  limit: number;
  used: number;
  hasSlot: boolean;
  // Go writes an empty list as null.
  consumers: SessionSlotConsumer[] | null;
}

export function getSessionSlots(options: { signal?: AbortSignal } = {}): Promise<SessionSlots> {
  return http.get<SessionSlots>("/sessions/current/session-slots", options);
}

export function revokeSessionSlot(kind: string, id: string): Promise<void> {
  return http.delete(`/sessions/current/session-slots/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`);
}

export function claimSessionSlot(): Promise<void> {
  return http.post("/sessions/current/claim-slot");
}
